import { inject, signal } from "@angular/core";
import { IngestionPoint } from "./data.ingestion.service";
import { IngestionPointCategory } from "./data.ingestion.const";
import { IngestionSecurityService } from "./ingestion.security.service";
import { SeSecurityStore } from "../student-engagement/se.security.store";
import { AccessLevels } from "../../pojo/Fls";
import { RoleRights } from "../student-engagement/securityConfig/role.rights";
import { LoginStore } from "../../stores/login.store";

/**
 * Role-based security implementation for ingestion forms.
 * Uses Student Engagement role rights (SE_FORMS_MANAGE) and form ownership
 * (mirrors server owner bypass in IngestionPointAccessService).
 */
export class RoleBasedIngestionSecurityService implements IngestionSecurityService {
    private securityStore = inject(SeSecurityStore);
    private loginStore = inject(LoginStore);

    canCreateForm(category: IngestionPointCategory | undefined, subCategory: string | undefined): boolean {
        // If global admin (FLS override), can create any form
        if (this.securityStore.hasFLSOverride()) {
            return true;
        }

        // Only ENGAGEMENT forms are organization-scoped
        if (category !== IngestionPointCategory.ENGAGEMENT || !subCategory) {
            return false;
        }

        // Can create if user has Full access to SE_FORMS_MANAGE
        const maxRights = this.securityStore.maxRights();
        if (!maxRights) {
            return false;
        }
        return maxRights[RoleRights.RoleRightsList.SE_FORMS_MANAGE] === AccessLevels.Full;
    }

    //Check that the user has SE_FORMS_MANAGE on the target org
    canManageForm(form: IngestionPoint): boolean {
        if (this.securityStore.hasFLSOverride()) {
            return true;
        }

        if (this.isFormOwner(form)) {
            return true;
        }

        // Only ENGAGEMENT forms are organization-scoped
        if (form.category !== IngestionPointCategory.ENGAGEMENT) {
            return false;
        }

        const orgId = form.subCat ? parseInt(form.subCat) : null;
        if (!orgId || isNaN(orgId)) {
            return false;
        }

        // Generic forms require SE_FORMS_MANAGE
        return !!this.securityStore.orgAccessSignal(
            signal(orgId),
            RoleRights.RoleRightsList.SE_FORMS_MANAGE,
            AccessLevels.Full,
        )();
    }

    canViewForm(form: IngestionPoint): boolean {
        if (this.securityStore.hasFLSOverride()) {
            return true;
        }

        if (this.isFormOwner(form)) {
            return true;
        }

        // Only ENGAGEMENT forms are organization-scoped
        if (form.category !== IngestionPointCategory.ENGAGEMENT) {
            return false;
        }

        const orgId = form.subCat ? parseInt(form.subCat) : null;
        if (!orgId || isNaN(orgId)) {
            return false;
        }

        return !!this.securityStore.orgAccessSignal(
            signal(orgId),
            RoleRights.RoleRightsList.SE_FORMS_MANAGE,
            AccessLevels.Read,
        )();
    }

    private isFormOwner(form: IngestionPoint): boolean {
        const ownerContId = form.ownerContId;
        const contactId = this.loginStore.user()?.userId;
        if (!ownerContId || !contactId) {
            return false;
        }
        return ownerContId === contactId;
    }
}
